import { Download, Eye, FileText } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { TaxFilingDocument } from '../types/taxFilings.types';

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

interface TaxFilingDocumentRowProps {
  document: TaxFilingDocument;
  badge?: ReactNode;
  actions?: ReactNode;
  getPreviewUrl: (documentId: string) => Promise<string | null>;
  meta?: ReactNode;
}

export function TaxFilingDocumentRow({
  document: doc,
  badge,
  actions,
  getPreviewUrl,
  meta,
}: TaxFilingDocumentRowProps) {
  const [busyAction, setBusyAction] = useState<'preview' | 'download' | null>(null);

  const handlePreview = async () => {
    setBusyAction('preview');
    try {
      const url = await getPreviewUrl(doc.id);
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } finally {
      setBusyAction(null);
    }
  };

  const handleDownload = async () => {
    setBusyAction('download');
    try {
      const url = await getPreviewUrl(doc.id);
      if (url) {
        const link = window.document.createElement('a');
        link.href = url;
        link.download = doc.name;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.click();
      }
    } finally {
      setBusyAction(null);
    }
  };

  return (
    <div
      className={cn(
        'flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-lg border bg-card',
        'hover:bg-muted/30 transition-colors'
      )}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <FileText className="h-5 w-5 text-muted-foreground shrink-0" />
        <div className="min-w-0">
          <p className="font-medium truncate">{doc.name}</p>
          <p className="text-xs text-muted-foreground">
            {formatFileSize(doc.size)}
            {meta}
          </p>
        </div>
        {badge}
      </div>
      <div className="flex items-center gap-2 shrink-0 flex-wrap">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="sm"
              variant="ghost"
              className="h-8 gap-1.5"
              onClick={handlePreview}
              disabled={busyAction === 'preview'}
            >
              {busyAction === 'preview' ? (
                <Spinner size="sm" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
              View
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p className="font-medium">View</p>
            <p className="text-muted-foreground text-xs">Open in new tab</p>
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="sm"
              variant="ghost"
              className="h-8 gap-1.5"
              onClick={handleDownload}
              disabled={busyAction === 'download'}
            >
              {busyAction === 'download' ? (
                <Spinner size="sm" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              Download
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p className="font-medium">Download</p>
            <p className="text-muted-foreground text-xs">Save file to your device</p>
          </TooltipContent>
        </Tooltip>
        {actions}
      </div>
    </div>
  );
}

interface TaxFilingEmptyStateProps {
  title: string;
  description: string;
}

export function TaxFilingEmptyState({ title, description }: TaxFilingEmptyStateProps) {
  return (
    <div className="rounded-lg border border-dashed py-12 text-center text-muted-foreground">
      <FileText className="mx-auto h-10 w-10 opacity-50 mb-2" />
      <p className="font-medium">{title}</p>
      <p className="text-sm mt-1">{description}</p>
    </div>
  );
}

export function TaxFilingLoadingState() {
  return (
    <div className="flex items-center justify-center py-12">
      <Spinner className="h-8 w-8 text-primary" />
    </div>
  );
}

export function TaxFilingErrorState({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-4 text-sm text-destructive">
      {message}
    </div>
  );
}

export function StatusBadge({
  label,
  variant = 'outline',
}: {
  label: string;
  variant?: 'secondary' | 'default' | 'destructive' | 'outline';
}) {
  return (
    <Badge variant={variant} className="shrink-0">
      {label}
    </Badge>
  );
}
